import { ChatChannels } from '@core/chat';
import { DisconnectReason, LiveStatus, SpeakerStatus } from '@core/types';
import { AccessDenied, Heading, Switch } from '@core/ui';
import { useFullscreenController, VindralPlayer } from '@online-webinar/player';
import { setSpeakerStatus, useAppDispatch, useAppSelector } from '@online-webinar/store';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoContent } from '../../molecules';
import { AudioAndVideoSelection } from '../AudioAndVideoSelection';

export const SpeakerWaitingRoom = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const liveStatus = useAppSelector((state) => state.liveWebinar.liveStatus);
    const mySpeakerStatus = useAppSelector((state) => state.liveWebinar.mySpeakerStatus);
    const disconnectReason = useAppSelector((state) => state.liveWebinar.dataConnection.disconnectReason);
    const notifiedAt = useAppSelector((state) => state.liveWebinar.notifiedAt);

    const [devicesSelected, setDevicesSelected] = useState(false);

    const videoContainerRef = useRef<HTMLDivElement | null>(null);
    const { toggleFullscreen, isFauxFullscreen, isFullscreen } = useFullscreenController(videoContainerRef);

    // Let the studio see the speaker enter as soon as the waiting room mounts
    useEffect(() => {
        dispatch(setSpeakerStatus(SpeakerStatus.SettingUp));
    }, [dispatch]);

    // notifiedAt is a monotonic timestamp (never reset in the slice), so we track the last
    // value we've already surfaced and only (re-)show the notice when it actually changes.
    const lastNotifiedAt = useRef<string | null>(null);
    const [showNotified, setShowNotified] = useState(false);
    useEffect(() => {
        if (!notifiedAt || notifiedAt === lastNotifiedAt.current) {
            return;
        }
        lastNotifiedAt.current = notifiedAt;
        setShowNotified(true);

        const timeout = setTimeout(() => setShowNotified(false), 8000);
        return () => clearTimeout(timeout);
    }, [notifiedAt]);

    if (disconnectReason === DisconnectReason.Kicked) {
        return (
            <div className="flex-1 max-w-4xl flex flex-col items-center justify-center mx-auto px-4 py-8 md:px-8 lg:px-12 text-center">
                <AccessDenied classNames="w-10 md:w-12 lg:w-16 stroke-warning mb-4" />
                <p>{t('Speaker.WaitingRoom.Kicked')}</p>
            </div>
        );
    }

    const ready = mySpeakerStatus === SpeakerStatus.Ready;

    return (
        <div className="flex flex-col gap-4 p-4">
            <Heading variant="h2">{t('Speaker.WaitingRoom.Heading')}</Heading>

            {showNotified && (
                <div className="bg-warning-light rounded-lg p-2 text-sm" role="status">
                    {t('Speaker.WaitingRoom.Notified')}
                </div>
            )}

            {!devicesSelected && (
                <AudioAndVideoSelection
                    disableExternalIngest
                    disableMoq
                    onAudioVideoSelect={() => {
                        setDevicesSelected(true);
                        dispatch(setSpeakerStatus(SpeakerStatus.NotReady));
                    }}
                />
            )}

            {devicesSelected && (
                <>
                    <div className="bg-warning-light rounded-lg p-2 text-sm">
                        {liveStatus === LiveStatus.Live
                            ? t('Speaker.WaitingRoom.Status.Live')
                            : ready
                              ? t('Speaker.WaitingRoom.Status.WaitingReady')
                              : t('Speaker.WaitingRoom.Status.WaitingNotReady')}
                    </div>

                    <div ref={videoContainerRef} className={isFauxFullscreen ? 'fixed inset-0 bg-black z-50' : 'relative max-h-[320px]'}>
                        <VideoContent>
                            <VindralPlayer isFullscreen={isFullscreen} isFauxFullscreen={isFauxFullscreen} toggleFullscreen={toggleFullscreen} />
                        </VideoContent>
                    </div>

                    <Switch
                        label={t('Speaker.WaitingRoom.ReadyToggle.Label')}
                        checked={ready}
                        onChange={() => dispatch(setSpeakerStatus(ready ? SpeakerStatus.NotReady : SpeakerStatus.Ready))}
                    />

                    <div className="h-96">
                        <ChatChannels />
                    </div>
                </>
            )}
        </div>
    );
};
